import { isExternalUrl } from "@dgs/core";
import { FormikErrors, useFormikContext } from "formik";
import { TFunction } from "i18next";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EventParticipationFormValues, EventTenantsPlannedPersonCountFormState } from "../../types/event";
import { EventFormCard } from "./EventFormCard";
import { EventParticipationStepFormFields } from "./EventParticipationStepFormFields";
import { getPrevLink, useSubmitEventStep } from "./eventStepperUtils";

export function getEventParticipationStepValidator(t: TFunction) {
	return (values: EventParticipationFormValues): FormikErrors<EventParticipationFormValues> => {
		const errors: FormikErrors<EventParticipationFormValues> = {};
		if (values.eventType?.participationPermissionRequirement === "required" && values.isInviteOnly === null) {
			errors.isInviteOnly = t("Field is required");
		}
		if (values.eventType?.linkToEventRequirement === "required" && !values.link) {
			errors.link = t("Field is required");
		}
		if (
			(values.eventType?.linkToEventRequirement === "required" ||
				values.eventType?.linkToEventRequirement === "optional") &&
			values.link &&
			!isExternalUrl(values.link)
		) {
			errors.link = t("Invalid url");
		}
		if (
			values.eventType?.plannedPersonCountRequirement === "required" &&
			values.tenantPlannedPersonCounts.length === 0
		) {
			errors.tenantPlannedPersonCounts = t("At least one planned person count must be specified.");
		}
		if (values.tenantPlannedPersonCounts.length > 0) {
			values.tenantPlannedPersonCounts.forEach((pc, i) => {
				if (pc.tenant === null || pc.plannedPersonCount === null) {
					const plannedPersonErrors: { [key in keyof EventTenantsPlannedPersonCountFormState]?: string } = {};

					if (pc.tenant === null) {
						plannedPersonErrors.tenant = t("Required field");
					}
					if (pc.plannedPersonCount === null) {
						plannedPersonErrors.plannedPersonCount = t("Required field");
					}
					if (!Array.isArray(errors.tenantPlannedPersonCounts)) {
						errors.tenantPlannedPersonCounts = [];
					}
					errors.tenantPlannedPersonCounts[i] = plannedPersonErrors;
				}
			});
		}
		return errors;
	};
}

export const isEventParticipationStepValid = (errors: FormikErrors<EventParticipationFormValues>) => {
	return !errors.isInviteOnly && !errors.link && !errors.tenantPlannedPersonCounts;
};

interface Props {
	variant?: "elevated" | "outlined";
}

export const EventParticipationStep: FC<Props> = ({ variant }) => {
	const { t } = useTranslation();
	const { values, errors, isValidating, setTouched, touched } = useFormikContext<EventParticipationFormValues>();
	const submitEventStep = useSubmitEventStep();

	const handleNextClick = useCallback(async () => {
		await setTouched({
			...touched,
			link: true,
			isInviteOnly: true,
			tenantPlannedPersonCounts: values.tenantPlannedPersonCounts.map(() => ({
				tenant: true,
				plannedPersonCount: true,
			})),
		});

		if (!isValidating && isEventParticipationStepValid(errors)) {
			await submitEventStep(values.eventType, "participation");
		}
	}, [errors, isValidating, setTouched, submitEventStep, touched, values.eventType, values.tenantPlannedPersonCounts]);

	return (
		<EventFormCard
			variant={variant}
			title={t("Participation")}
			prevLink={getPrevLink(values.eventType, "participation")}
			onNextClick={handleNextClick}
			showLanguageSwitcher={false}
		>
			<EventParticipationStepFormFields />
		</EventFormCard>
	);
};
